import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Offer to install the app.
 *
 * Android and desktop Chrome fire beforeinstallprompt and hand us a prompt to
 * call. iOS fires nothing and has no API — the only route is Share → Add to
 * Home Screen — so Safari on an iPhone gets instructions instead of a button.
 *
 * Dismissal is remembered, because an install banner that returns on every
 * visit is an advert.
 */
const DISMISSED = "erp4u.install.dismissed";

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches
  || window.navigator.standalone === true;

const isIosSafari = () => {
  const ua = window.navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua)
    || (ua.includes("Macintosh") && "ontouchend" in document);   // iPadOS lies
  return ios && !/CriOS|FxiOS|EdgiOS/.test(ua);
};

export default function InstallApp() {
  const [prompt, setPrompt] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [hidden, setHidden] = useState(true);

  useEffect(() => {
    if (isStandalone()) return undefined;             // already installed
    try {
      if (localStorage.getItem(DISMISSED)) return undefined;
    } catch { /* private mode — just show it */ }

    const onPrompt = (e) => {
      e.preventDefault();
      setPrompt(e);
      setHidden(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", () => setHidden(true));

    // Safari never fires the event, so offer the manual route instead.
    if (isIosSafari()) {
      setShowIos(true);
      setHidden(false);
    }
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    setHidden(true);
    try { localStorage.setItem(DISMISSED, "1"); } catch { /* nothing to do */ }
  };

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome !== "dismissed") setHidden(true);
    setPrompt(null);
  };

  if (hidden) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white p-3 shadow-[0_-4px_16px_rgba(15,23,42,0.08)] sm:inset-x-auto sm:bottom-4 sm:right-4 sm:max-w-sm sm:rounded-lg sm:border"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      data-testid="install-app-banner">
      <div className="flex items-start gap-3">
        <img src="/icon-192.png" alt="" className="h-10 w-10 shrink-0 rounded-lg" />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">Install ERP4U Billing</div>
          {showIos ? (
            <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
              Tap <Share size={11} className="inline -mt-0.5" /> Share, then
              <strong> Add to Home Screen</strong>, to bill from your home screen
              without opening Safari.
            </p>
          ) : (
            <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
              Add it to your home screen and bill straight from the icon — full
              screen, and it opens faster.
            </p>
          )}
        </div>
        <button onClick={dismiss} data-testid="install-dismiss"
          className="-mr-1 -mt-1 shrink-0 rounded-md p-2 text-slate-400 transition-colors duration-150 hover:text-slate-700">
          <X size={16} />
        </button>
      </div>
      {!showIos && (
        <Button onClick={install} data-testid="install-app-btn"
          className="mt-3 w-full gap-1.5 bg-blue-600 hover:bg-blue-700">
          <Download size={15} /> Install app
        </Button>
      )}
    </div>
  );
}
